/**
 * InMemoryKnowledgeGraph - In-memory implementation of KnowledgeGraph.
 *
 * This implementation stores individuals and relationships in JavaScript Maps.
 * It provides SPARQL query capability via Comunica against an N3 store.
 *
 * This class preserves the original synchronous-style behavior of KnowledgeGraph,
 * with the storage operations wrapped in async methods to conform to the abstract interface.
 */
import { Store, DataFactory } from 'n3';
import { QueryEngine } from '@comunica/query-sparql';
import KnowledgeGraph from "./KnowledgeGraph";
import Individual from "./Individual";
import Relationship from "./Relationship";
const { namedNode, literal, quad: createQuad } = DataFactory;
/**
 * RDF namespace constants
 */
const RDF_TYPE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';
const RDFS_LABEL = 'http://www.w3.org/2000/01/rdf-schema#label';
const RDFS_COMMENT = 'http://www.w3.org/2000/01/rdf-schema#comment';
const OWL_NAMED_INDIVIDUAL = 'http://www.w3.org/2002/07/owl#NamedIndividual';
/**
 * InMemoryKnowledgeGraph - Stores individuals and relationships in memory.
 *
 * This is the default implementation that preserves backward compatibility
 * with the original KnowledgeGraph class behavior.
 */
export default class InMemoryKnowledgeGraph extends KnowledgeGraph {
    constructor(name, uri, ontologySchema, id, boundedContextId) {
        super(name, uri, ontologySchema, id, boundedContextId);
        /** In-memory storage for individuals */
        Object.defineProperty(this, "_individuals", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        /** In-memory storage for relationships */
        Object.defineProperty(this, "_relationships", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        /** Comunica query engine (lazy initialized) */
        Object.defineProperty(this, "_queryEngine", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
    }
    /**
     * Get the query engine (lazy initialization).
     */
    getQueryEngine() {
        if (!this._queryEngine) {
            this._queryEngine = new QueryEngine();
        }
        return this._queryEngine;
    }
    // =========================================================================
    // Template method implementations for in-memory storage
    // =========================================================================
    async loadIndividuals() {
        return Array.from(this._individuals.values());
    }
    async loadRelationships() {
        return Array.from(this._relationships.values());
    }
    async storeIndividual(individual) {
        this._individuals.set(individual.name, individual);
    }
    async deleteIndividual(individual) {
        this._individuals.delete(individual.name);
    }
    async storeRelationship(relationship) {
        this._relationships.set(relationship.id, relationship);
    }
    async deleteRelationship(relationship) {
        this._relationships.delete(relationship.id);
    }
    // =========================================================================
    // SPARQL query implementations via Comunica
    // =========================================================================
    /**
     * Convert in-memory individuals and relationships to an N3 Store for querying.
     */
    toN3Store() {
        const store = new Store();
        // Add individuals
        for (const individual of this._individuals.values()) {
            // rdf:type owl:NamedIndividual
            store.addQuad(createQuad(namedNode(individual.uri), namedNode(RDF_TYPE), namedNode(OWL_NAMED_INDIVIDUAL)));
            // rdf:type <classUri>
            store.addQuad(createQuad(namedNode(individual.uri), namedNode(RDF_TYPE), namedNode(individual.modelClass.uri)));
            // rdfs:label
            store.addQuad(createQuad(namedNode(individual.uri), namedNode(RDFS_LABEL), literal(individual.name)));
            // rdfs:comment (definition)
            if (individual.definition) {
                store.addQuad(createQuad(namedNode(individual.uri), namedNode(RDFS_COMMENT), literal(individual.definition)));
            }
            // Data property values
            individual.propertyValues.forEach((values, propertyUri) => {
                for (const value of values) {
                    store.addQuad(createQuad(namedNode(individual.uri), namedNode(propertyUri), literal(value)));
                }
            });
        }
        // Add relationships
        for (const rel of this._relationships.values()) {
            store.addQuad(createQuad(namedNode(rel.subject.uri), namedNode(rel.predicate.uri), namedNode(rel.object.uri)));
        }
        return store;
    }
    async select(sparql) {
        const store = this.toN3Store();
        const engine = this.getQueryEngine();
        const bindingsStream = await engine.queryBindings(sparql, { sources: [store] });
        const bindingsArray = await bindingsStream.toArray();
        // Extract variable names
        const vars = [];
        if (bindingsArray.length > 0) {
            for (const key of bindingsArray[0].keys()) {
                vars.push(key.value);
            }
        }
        const bindings = bindingsArray.map((binding) => {
            const result = {};
            for (const [variable, term] of binding) {
                if (term) {
                    result[variable.value] = {
                        value: term.value,
                        type: term.termType === 'NamedNode' ? 'uri' :
                            term.termType === 'BlankNode' ? 'bnode' : 'literal',
                        ...(term.termType === 'Literal' && term.datatype && {
                            datatype: term.datatype.value,
                        }),
                        ...(term.termType === 'Literal' && term.language && {
                            'xml:lang': term.language,
                        }),
                    };
                }
            }
            return result;
        });
        return {
            head: { vars },
            results: { bindings },
        };
    }
    async ask(sparql) {
        const store = this.toN3Store();
        const engine = this.getQueryEngine();
        return engine.queryBoolean(sparql, { sources: [store] });
    }
    async construct(sparql) {
        const store = this.toN3Store();
        const engine = this.getQueryEngine();
        const quadsStream = await engine.queryQuads(sparql, { sources: [store] });
        return quadsStream.toArray();
    }
    // =========================================================================
    // Direct access to Maps (for backward compatibility)
    // =========================================================================
    /**
     * Get direct access to the individuals Map.
     * @deprecated Use async methods instead
     */
    get individuals() {
        return this._individuals;
    }
    /**
     * Get direct access to the relationships Map.
     * @deprecated Use async methods instead
     */
    get relationships() {
        return this._relationships;
    }
    /**
     * Synchronous lookup of relationship by ID.
     * @deprecated Use getRelationshipById (async) for portable code
     */
    getRelationshipByIdSync(id) {
        return this._relationships.get(id);
    }
    /**
     * Synchronous lookup of individual by URI.
     * @deprecated Use getNodeByUri (async) for portable code
     */
    getNodeByUriSync(uri) {
        return Array.from(this._individuals.values()).find(i => i.uri === uri);
    }
    /**
     * Synchronous lookup of individuals by class URI.
     * @deprecated Use getIndividualsByClassUri (async) for portable code
     */
    getIndividualsByClassUriSync(classUri) {
        return Array.from(this._individuals.values()).filter(individual => this.isSubclassOf(individual.modelClass.uri, classUri));
    }
    // =========================================================================
    // Serialization
    // =========================================================================
    static fromJSON(json, vocabulary, modelClassResolver, objectPropertyResolver, tboxIndividualResolver) {
        const knowledgeGraph = new InMemoryKnowledgeGraph(json.name, json.uri, undefined, json.id, json.boundedContextId);
        // Restore individuals
        (json.individuals ?? []).forEach((individual) => {
            knowledgeGraph._individuals.set(individual.individualLabel || individual.concept?.prefLabel, Individual.fromJSON(individual, vocabulary, modelClassResolver));
        });
        // Restore relationships (after individuals are loaded)
        const relationshipsJson = json.relationships ?? json.assertions ?? [];
        relationshipsJson.forEach((relJson) => {
            const relationship = Relationship.fromJSON(relJson, (uri) => knowledgeGraph.getNodeByUriSync(uri) || tboxIndividualResolver?.(uri), objectPropertyResolver || (() => undefined));
            if (relationship) {
                knowledgeGraph._relationships.set(relationship.id, relationship);
            }
        });
        // Restore ontology imports
        if (json.imports) {
            for (const imp of json.imports) {
                knowledgeGraph.addImport(imp.ontologyIri, imp.versionIri);
            }
        }
        // Restore namespace prefixes
        if (json.prefixes) {
            for (const pref of json.prefixes) {
                knowledgeGraph.addPrefix(pref.namespace, pref.prefix);
            }
        }
        return knowledgeGraph;
    }
}
export { InMemoryKnowledgeGraph };
